import * as React from "react";

import {
  baseLocale,
  cookieDomain,
  cookieMaxAge,
  cookieName,
  getLocale as runtimeGetLocale,
  getTextDirection,
  isLocale,
  locales,
  overwriteGetLocale,
} from "@/paraglide/runtime.js";

const LOCALE_STORAGE_KEY = "anthem-internal-locale";

type AppLocale = (typeof locales)[number];

let activeLocale: AppLocale = baseLocale;

interface LocaleContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
}

function getStoredLocale(): AppLocale | null {
  if (typeof window === "undefined") return null;

  try {
    const storage = window.localStorage;
    if (!storage || typeof storage.getItem !== "function") {
      return null;
    }

    const stored = storage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

function getInitialLocale(): AppLocale {
  const stored = getStoredLocale();
  if (stored) return stored;

  try {
    const resolved = runtimeGetLocale();
    return isLocale(resolved) ? resolved : baseLocale;
  } catch {
    return baseLocale;
  }
}

function applyLocale(locale: AppLocale, persist: boolean) {
  activeLocale = locale;

  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
    document.documentElement.dir = getTextDirection(locale);
    const domain = cookieDomain ? `; domain=${cookieDomain}` : "";
    document.cookie = `${cookieName}=${locale}; path=/; max-age=${cookieMaxAge}${domain}`;
  }

  if (persist && typeof window !== "undefined") {
    try {
      const storage = window.localStorage;
      if (storage && typeof storage.setItem === "function") {
        storage.setItem(LOCALE_STORAGE_KEY, locale);
      }
    } catch {
      // Ignore storage failures in tests and restricted browser contexts.
    }
  }
}

activeLocale = getInitialLocale();
overwriteGetLocale(() => activeLocale);

if (typeof window !== "undefined") {
  applyLocale(activeLocale, false);
}

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<AppLocale>(() => activeLocale);

  React.useEffect(() => {
    applyLocale(locale, true);
  }, [locale]);

  const setLocale = React.useCallback((nextLocale: AppLocale) => {
    applyLocale(nextLocale, true);
    setLocaleState((currentLocale) => {
      if (currentLocale === nextLocale) return currentLocale;
      return nextLocale;
    });
  }, []);

  const value = React.useMemo<LocaleContextValue>(
    () => ({ locale, setLocale }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useAppLocale() {
  const context = React.useContext(LocaleContext);
  if (!context) {
    throw new Error("useAppLocale must be used within LocaleProvider");
  }
  return context;
}

export { locales };
export type { AppLocale };
